/* eslint-disable react/prop-types */
import React, { useContext, useRef, useEffect, useMemo } from 'react';
import cx from 'utils/classnames';
import useOnScreen from 'hooks/custom/useOnScreen';
import GlobalContext from 'hooks/contexts/GlobalContext';
import Rating from 'components/base/rating/Rating';
import ToolTip from 'components/base/toolTip/ToolTip';
import { QUERY_PARAMS, RATING_TOOLTIP_TEXT } from 'consts';
import { CategoryGuid, GetSchoolListingResults, SchoolResult } from 'types';
import ResultCardLink from 'components/sections/lists/ClickPortalResults/result-card/ResultCardLink';
import ResultCardContext from 'components/sections/lists/ClickPortalResults/result-card/ResultCardContext';
import ResultCardLogo from 'components/sections/lists/ClickPortalResults/result-card/ResultCardLogo';
import ResultCardTitle from 'components/sections/lists/ClickPortalResults/result-card/ResultCardTitle';
import useAppendQueryParams from 'hooks/custom/useAppendQueryParams';
import isEmpty from 'lodash/isEmpty';
import ResponsiveImage from 'components/base/responsiveImage/ResponsiveImage';
import useListingResultTracking from '../useListingResultTracking';

interface SchoolCardProps {
  index: number;
  schoolInfo: SchoolResult;
  isLoading: boolean;
  rowCategoryGuid: CategoryGuid | null;
  badge: string;
  rowHasTags: boolean;
  rowIndex: number;
}

const responsiveImageSizes = {
  MOBILE: '90vw',
  TABLET: '310px',
  DESKTOP: '310px',
  DEFAULT: '90vw',
};

export default function SchoolCard({
  rowCategoryGuid,
  schoolInfo,
  isLoading,
  badge,
  rowHasTags,
  index,
  rowIndex,
}: SchoolCardProps): React.JSX.Element {
  const ref = useRef<HTMLElement>(null);
  const {
    windowSize: { currentBreakpoint = 'mobile' },
  } = useContext(GlobalContext);
  const hasBeenSeenRef = useRef(false);
  const { wasOnScreenOnLoad, wasWithinViewPortOnLoad, isOnScreen } =
    useOnScreen(ref, '-100px');
  const isMobile = currentBreakpoint === 'mobile';
  const { IMPRESSION_GUID_PARAM } = QUERY_PARAMS;

  // TODO: Do we need this here? Should this and resultcard share this logic in some other file like a hook?
  const isProgramView =
    Object.keys(schoolInfo.selectedProgram || {}).length > 0;
  const currentLink = isProgramView
    ? (schoolInfo?.selectedProgram?.destinationUrl as string)
    : (schoolInfo?.url as string);

  function isMatch(
    school: GetSchoolListingResults['results'][number]
  ): boolean {
    // If the row has tags and the school has a filter tag, the school is a match
    if (rowHasTags && school.filterTagName) {
      return true;
    }

    // If the row has tags and the school does not have a filter tag, the school is not a match
    if (rowHasTags && !school.filterTagName) {
      return false;
    }

    // If the row does not have tags and the school has a matching category, the school is a match
    const { matchingCategory } = school;
    const matchingConcentration = rowCategoryGuid;

    return !matchingConcentration || matchingCategory === matchingConcentration;
  }

  const isBestMatching = !isEmpty(schoolInfo?.selectedProgram);

  const listingResultTracking = useListingResultTracking(
    schoolInfo,
    isBestMatching
  );

  const handleLogoClick = (): void => {
    listingResultTracking.clickedLogo();
  };

  const handleTitleClick = (): void => {
    listingResultTracking.clickedTitle();
  };

  const handleLearnMoreClick = (): void => {
    listingResultTracking.clickedLearnMore();
  };

  const handleProgramLinkClick = (): void => {
    listingResultTracking.clickedProgramLink();
  };

  const learnMoreLink = useAppendQueryParams(currentLink, {
    [IMPRESSION_GUID_PARAM]: schoolInfo.impressionGuid,
    click_location: isProgramView
      ? 'best matching card | learn-more'
      : 'school card | learn-more',
  });

  const bestMatchingProgramLink = useAppendQueryParams(currentLink, {
    [IMPRESSION_GUID_PARAM]: schoolInfo.impressionGuid,
    click_location: 'best matching card | best matching link',
  });

  useEffect(() => {
    const onscreenTimeout = setTimeout(() => {
      if (isOnScreen && !hasBeenSeenRef.current) {
        hasBeenSeenRef.current = true;
        listingResultTracking.viewedResult(
          wasOnScreenOnLoad,
          wasWithinViewPortOnLoad
        );
      }
    }, 500);
    return () => clearTimeout(onscreenTimeout);
  }, [isOnScreen]);

  const contextValue = useMemo(
    () => ({
      listingResultTracking,
      index,
      schoolName: schoolInfo?.name,
      isOffsiteConversion: schoolInfo?.isOffsiteConversion,
    }),
    [
      listingResultTracking,
      index,
      schoolInfo?.name,
      schoolInfo?.isOffsiteConversion,
    ]
  );
  function shouldLazyLoad(): boolean {
    // mobile, only 1 row visible at a time
    // lazy load after 2nd row
    if (isMobile) {
      if (rowIndex > 1) {
        return true;
      }
      // mobile, first 2 rows, lazy load after first 2 cards
      return index > 1;
    }

    // desktop, lazy load after 3rd row
    if (rowIndex > 2) {
      return true;
    }

    // desktop, first 5 rows, lazy load after 5th card
    return index > 4;
  }

  return (
    <ResultCardContext.Provider value={contextValue}>
      <article
        ref={ref as React.RefObject<HTMLElement>}
        data-testid="schoolCard"
        className={cx('schoolCard', {
          'schoolCard--isLoading': isLoading,
          'schoolCard--mobile': isMobile,
          'schoolCard--isProgramView': isProgramView,
        })}
      >
        <div className="schoolCard__header">
          {isMatch(schoolInfo) && badge && (
            <span className="schoolCard__badge schoolCard__badge--editorsChoice">
              {badge}
            </span>
          )}
          {!isMatch(schoolInfo) && (
            <span className="schoolCard__badge schoolCard__badge--notMatch">
              Related Program
            </span>
          )}
          <div className="schoolCard__image">
            <ResponsiveImage
              images={{ image: schoolInfo?.featuredImage.desktop || {} }}
              sizes={responsiveImageSizes}
              alt={`${schoolInfo.name} campus`}
              className="schoolCard__heroImage"
              loading={shouldLazyLoad() ? 'lazy' : 'eager'}
            />
          </div>
          <ResultCardTitle
            className="schoolCard__title"
            impressionGuid={schoolInfo.impressionGuid}
            isProgramView={isProgramView}
            linkUrl={currentLink}
            onClick={handleTitleClick}
          >
            {schoolInfo.name}
          </ResultCardTitle>
        </div>
        <div className="schoolCard__content">
          <div className="schoolCard__info">
            <div className="schoolCard__logo">
              <ResultCardLogo
                isLoading={isLoading}
                linkUrl={currentLink}
                logoImg={schoolInfo?.schoolLogoUrl?.desktop}
                onClick={handleLogoClick}
                impressionGuid={schoolInfo.impressionGuid}
                className="schoolCard__logo"
                isProgramView={isProgramView}
              />
            </div>
            <div className="schoolCard__rating">
              <div className="schoolCard__ratingLabel">
                <span className="schoolCard__ratingLabelText">
                  Editor&apos;s Rating
                </span>
                <span className="schoolCard__ratingToolTip">
                  <ToolTip label="Editor's Rating" positioning="top">
                    {RATING_TOOLTIP_TEXT}
                  </ToolTip>
                </span>
              </div>
              <Rating
                rating={schoolInfo?.rating || 0}
                id={`${schoolInfo?.id}-rating`}
              />
            </div>
          </div>
          <ResultCardLink
            linkUrl={bestMatchingProgramLink}
            className="schoolCard__programName"
            onClick={handleProgramLinkClick}
          >
            {schoolInfo?.selectedProgram?.label as string}
          </ResultCardLink>
        </div>
        <div className="schoolCard__actions">
          <ResultCardLink
            linkUrl={learnMoreLink}
            className="schoolCard__ctaButton"
            onClick={handleLearnMoreClick}
          >
            {isProgramView ? 'Visit Site' : 'Find Your Program'}
          </ResultCardLink>
        </div>
      </article>
    </ResultCardContext.Provider>
  );
}
